import React, { PureComponent } from 'react'
import each from 'lodash/each';
import orderBy from 'lodash/orderBy';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import Tooltip from '@mui/material/Tooltip'

import Button from './material/Button'
import AppButton from './material/AppButton'
import { radius } from '../theme'

import './styles/mobileTable.scss'

const DEFAULTS = {
  PAGE: 0,
  ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPTIONS: [10, 25, 50, 100],
}

export const CELL_TYPES = {
  BUTTON: 'BUTTON',
  BUTTON_ARR: 'BUTTON_ARR',
  BUTTON_ICON_ARR: 'BUTTON_ICON_ARR',
  LINK: 'LINK',
  TEXT: 'TEXT',
  TEXT_CENTER: 'TEXT_CENTER',
  HOLLOW_BUTTON: 'HOLLOW_BUTTON',
  ICON_BUTTON: 'ICON_BUTTON',
  FIELD: 'FIELD',
}

class PaginatedTable extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      page: this.props.page || DEFAULTS.PAGE,
      rowsPerPage: this.props.rowsPerPage || DEFAULTS.ROWS_PER_PAGE,
      sortBy: null,
      sortDirection: 'asc',
    }
    this.renderCell = this.renderCell.bind(this)
    this.changePage = this.changePage.bind(this)
    this.changeRowsPerPage = this.changeRowsPerPage.bind(this)
    this.handleSort = this.handleSort.bind(this)
  }

  changeRowsPerPage(event) {
    const next = parseInt(event.target.value, 10) || DEFAULTS.ROWS_PER_PAGE
    // Voltar pra página 0 ao trocar size — comportamento padrão MUI.
    this.setState({ rowsPerPage: next, page: 0 })
  }

  handleSort(column) {
    const { sortBy, sortDirection } = this.state
    const newDirection =
      sortBy === column.name && sortDirection === 'asc' ? 'desc' : 'asc'
    this.setState({ sortBy: column.name, sortDirection: newDirection })
  }

  changePage(event, page) {
    this.setState({ page })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({
        page: this.props.page || DEFAULTS.PAGE,
      })
    }
  }

  handleLabelFlags(label) {
    if (label?.indexOf('{bold}') >= 0) {
      return <b>{label.replace('{bold} ', '')}</b>
    }
    if (label?.indexOf('{white}') >= 0) {
      return <b style={{ color: 'white' }}>{label.replace('{white}', '')}</b>
    }
    return label
  }

  async exportData(data) {
    const str = JSON.stringify(data)
    const bytes = new TextEncoder().encode(str)
    const blob = new Blob([bytes], {
      type: 'application/json;charset=utf-8',
    })

    const newHandle = await window.showSaveFilePicker()
    const writableStream = await newHandle.createWritable()
    await writableStream.write(blob)
    await writableStream.close()
  }

  renderCell(
    type,
    { label, onClick, buttons, icon, field, useGridSystem, disabled },
    columnName,
    index
  ) {
    switch (type) {
      case CELL_TYPES.BUTTON:
        return (
          <TableCell key={`${columnName}_${index}`} align='right'>
            <Button
              style={useGridSystem ? { width: '100%' } : undefined}
              small
              onClick={onClick}
              label={label}
              disabled={disabled}
            />
          </TableCell>
        )
      case CELL_TYPES.LINK:
        return (
          <TableCell
            key={`${columnName}_${index}`}
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationColor: 'primary.main',
              width: this.props.columnWidth,
            }}
            onClick={onClick}
          >
            {label}
          </TableCell>
        )
      case CELL_TYPES.BUTTON_ARR:
        return (
          <TableCell key={`${columnName}_${index}`} align='right'>
            {buttons.map((button, bIndex) =>
              button.icon ? (
                <Tooltip
                  key={`${columnName}_${index}_${bIndex}`}
                  title={button.label}
                  arrow
                  placement='top'
                >
                  <IconButton
                    style={{ marginLeft: '0.2em' }}
                    onClick={button.onClick}
                    size="small">
                    {button.icon}{' '}
                  </IconButton>
                </Tooltip>
              ) : (
                <AppButton
                  key={`${columnName}_${index}_${bIndex}`}
                  size="small"
                  variant="primary"
                  onClick={button.onClick}
                  disabled={button.disabled}
                  sx={{ mx: 0.5 }}
                >
                  {button.label}
                </AppButton>
              )
            )}
          </TableCell>
        );
      case CELL_TYPES.ICON_BUTTON:
        return (
          <TableCell key={`${columnName}_${index}`} align='right'>
            <Tooltip title={label} arrow placement='top'>
              <IconButton key={`${columnName}_${index}`} onClick={onClick} size="small">
                {icon}
              </IconButton>
            </Tooltip>
          </TableCell>
        );
      case CELL_TYPES.HOLLOW_BUTTON:
        return (
          <TableCell key={`${columnName}_${index}`} align='right'>
            {<IconButton onClick={onClick} size="small">{icon}</IconButton>}
          </TableCell>
        );
      case CELL_TYPES.FIELD:
        return <TableCell key={`${columnName}_${index}`}>{field}</TableCell>
      case CELL_TYPES.TEXT_CENTER:
        return (
          <TableCell key={`${columnName}_${index}`} align='center'>
            {this.handleLabelFlags(label)}
          </TableCell>
        )
      default:
        return (
          <TableCell
            style={{ width: this.props.columnWidth }}
            key={`${columnName}_${index}`}
          >
            {this.handleLabelFlags(label)}
          </TableCell>
        )
    }
  }

  render() {
    let useGridSystem = false
    let proportions = {}

    each(this.props.columns, (column, index) => {
      if (column.grid) {
        useGridSystem = true
        proportions[index] =
          column.grid &&
          column.grid > 0 &&
          `${Math.floor((100 * column.grid) / 12)}%`
      }
    })

    // Sort local quando o usuário clicou num cabeçalho `sortBy: true`.
    // sortValue (custom) tem prioridade sobre label (fallback) para suportar
    // ordenação numérica em colunas formatadas (datas, taxas, contagens).
    //
    // pinLastRow: a última linha (ex: linha de Totais nos relatórios) é fixada
    // como último elemento — separada antes do sort e reanexada no final.
    const { sortBy, sortDirection } = this.state
    const sortColumn = sortBy
      ? this.props.columns.find((c) => c.name === sortBy)
      : null
    let sourceData
    if (sortColumn) {
      const rows = this.props.data
      const pinned = this.props.pinLastRow && rows.length > 0 ? rows[rows.length - 1] : null
      const sortable = pinned ? rows.slice(0, -1) : rows
      const sorted = orderBy(
        sortable,
        [(row, i) => (sortColumn.sortValue ? sortColumn.sortValue(row, i) : sortColumn.label(row, i))],
        [sortDirection],
      )
      sourceData = pinned ? [...sorted, pinned] : sorted
    } else {
      sourceData = this.props.data
    }

    const data = !this.props.skipPagination
      ? sourceData.slice(
        this.state.page * this.state.rowsPerPage,
        (this.state.page + 1) * this.state.rowsPerPage
      )
      : sourceData

    // Container radius token md (8px) — alinhado com Paper/Card globais via theme.
    const style = {
      borderRadius: radius.md,
      overflow: 'hidden', // garante que o header laranja respeite o radius do container
      ...this.props.style,
    }

    return (
      <TableContainer component={Paper} style={style}>
        <Table
          aria-label='custom pagination table'
          size={this.props.size}
        >
          <TableHead
            sx={{
              // Sticky header: gruda no topo do scroll container (AppShell <main>).
              // TopActionBar/PageHeader são irmãos no flex column, FORA do <main>,
              // então top: 0 = topo visível do scroll, sem offset de 48px.
              position: { md: 'sticky' },
              top: { md: 0 },
              zIndex: 2,
              // Header laranja Zoetis (2026-05-10) — padroniza com o look das
              // tabelas de relatório. Compacta padding pra ganhar densidade.
              // Relatórios preservados via .printableTableContainer th do
              // customstyle.scss (especificidade maior + tom levemente distinto).
              bgcolor: 'primary.main',
              '& .MuiTableCell-head': {
                color: 'common.white',
                fontWeight: 600,
                fontSize: '0.8125rem',  // 13px
                letterSpacing: '0.02em',
                lineHeight: 1.3,
                py: 1.25,  // 10px vertical (compacto)
                px: 1.5,   // 12px horizontal
                borderBottom: 'none',
              },
              '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active': {
                color: 'common.white',
              },
              '& .MuiTableSortLabel-icon': {
                color: 'common.white !important',
              },
            }}
          >
            <TableRow>
              {this.props.columns.map((column, index) => {
                const align = column.type === CELL_TYPES.TEXT_CENTER ? 'center' : 'left'
                const isSortable = !!column.sortBy
                const isActive = sortBy === column.name
                const cellContent = isSortable ? (
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? sortDirection : 'asc'}
                    onClick={() => this.handleSort(column)}
                  >
                    {column.name}
                  </TableSortLabel>
                ) : (
                  column.name
                )
                return (
                  <TableCell
                    width={useGridSystem ? proportions[index] : undefined}
                    key={`${column.name}_${index}`}
                    align={align}
                    style={align === 'left' ? { width: this.props.columnWidth } : undefined}
                    sortDirection={isActive ? sortDirection : false}
                  >
                    {column.name ? cellContent : null}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              // Zebra rows + hover state
              '& tr:nth-of-type(odd)': {
                bgcolor: 'rgba(234, 93, 13, 0.025)',
              },
              '& tr:hover': {
                bgcolor: 'rgba(234, 93, 13, 0.06)',
              },
              '& tr': {
                transition: 'background-color 120ms ease',
              },
              // Body compacto: 8px 12px (era ~16px default MUI)
              '& .MuiTableCell-body': {
                py: 1,
                px: 1.5,
                fontSize: '0.875rem',  // 14px
              },
            }}
          >
            {this.props.data.length > 0 ? (
              data.map((row, i) => {
                return (
                  <TableRow key={i}>
                    {this.props.columns.map((column) =>
                      this.renderCell(
                        column.type,
                        {
                          label: column.label ? column.label(row, i) : '',
                          onClick: column.onClick && column.onClick(row, i),
                          buttons: column.buttons
                            ? column.buttons(row, i)
                            : [],
                          icon: column.icon && column.icon(row, i),
                          field: column.field && column.field(row, i),
                          disabled:
                            column.disabled && column.disabled(row, i),
                          useGridSystem,
                        },
                        column.name,
                        i
                      )
                    )}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={this.props.columns.length} align='center'>
                  <Typography variant='h6'>
                    {this.props.noDataMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {!this.props.skipPagination && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={this.props.columns.length}
                  count={this.props.data.length}
                  rowsPerPage={this.state.rowsPerPage}
                  page={this.state.page}
                  onPageChange={this.changePage}
                  onRowsPerPageChange={this.changeRowsPerPage}
                  rowsPerPageOptions={
                    this.props.rowsPerPageOptions || DEFAULTS.ROWS_PER_PAGE_OPTIONS
                  }
                  labelRowsPerPage='Linhas por página'
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to === -1 ? count : to} de ${count !== -1 ? count : to}`
                  }
                  getItemAriaLabel={(type) => {
                    if (type === 'first') return 'Primeira página'
                    if (type === 'last') return 'Última página'
                    if (type === 'next') return 'Próxima página'
                    return 'Página anterior'
                  }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    )
  }
}

export default PaginatedTable
